import type {
  BidVsActualRow,
  CascadePhase,
  CrewLoadingRow,
  PhaseLifecycleStatus,
  ProductionRow,
  ProjectPhase,
} from '@frontend/AppSettings/services/PipelineProjectService';

/**
 * The pipeline schedule Gantt feed — the demo's PhaseSchedule layout, over THIS branch's
 * payloads. Design §4 D.5 kept the production AmCharts Gantt on the cross-project Schedule
 * page; this module is the converted-project tab's own chart, because a phase has a WINDOW
 * (planned_start → planned_end) and a milestone has a point, and the demo bars are windows.
 *
 * Named `phaseGanttModel` so it never shares a path with the component (`PipelinePhaseSchedule.tsx`)
 * on a case-insensitive filesystem.
 *
 * Money-free by default: budget/projected ride an OPTIONAL `bvaRows` argument, which the tab
 * only passes after narrowing `permitted`. A blind caller gets geometry and quantities, never
 * a $0 stand-in. Weather is the recorded skip (per-ticket free text, no impact rollup) —
 * there is no weather-day overlay to compute.
 */

export const MS_PER_DAY = 86_400_000;

export type GanttStatus = 'green' | 'yellow' | 'red' | 'future';

export interface CalendarSpan {
  /** Inclusive 1-based day of today within the span; clamped to [1, dayY]. */
  dayX: number;
  /** Inclusive calendar-day length of the span (and of any FA push past the last end). */
  dayY: number;
  /** UTC ms of the earliest planned_start. */
  startMs: number;
}

export interface PhaseGanttRow {
  id: string;
  name: string;
  sequence: number;
  /** 1-based day within the span; null when the phase has no window. */
  startDay: number | null;
  endDay: number | null;
  durationDays: number | null;
  windowStart: string | null;
  windowEnd: string | null;
  leftPct: number | null;
  widthPct: number | null;
  percentComplete: number | null;
  status: GanttStatus;
  statusLabel: string;
  /** Recorded FA days on THIS phase — the dashed-red slip overlay. */
  slipDays: number;
  /** Downstream push from the cascade — the dashed-amber overlay past planned_end. */
  pushDays: number;
  crewPlanned: number | null;
  crewActual: number | null;
  /** Cost codes on this phase's baseline lines (display). Falls back to the BvA name match. */
  costCodes: string[];
  /** Primary code for money/BvA — first baseline code, else the matched BvA row. */
  costCode: string | null;
  planned: number | undefined;
  projected: number | undefined;
  /** Payload `projected_variance` (planned − projected; negative = over) — same sign as the BvA table. */
  projectedVariance: number | undefined;
  /** Entered actual dates (0028) — reality beside the frozen window, never replacing it. */
  actualStart: string | null;
  actualEnd: string | null;
  /** The actual BAR overlaid on the planned row: entered start → entered end (or today while
   * open). Geometry only — it borrows no `GanttStatus` colour and repaints nothing. */
  actualLeftPct: number | null;
  actualWidthPct: number | null;
  /** The server-derived lifecycle WORDS (0028's one sanctioned derivation). Deliberately a
   * separate axis from `status`/`statusLabel` (the money/FA paint): a phase with an early first
   * sheet is "In progress" in words over a still-future-painted bar, and both are true. */
  lifecycle: PhaseLifecycleStatus;
  lifecycleLabel: string;
  /** Sheet EVIDENCE — first/last day-sheet date on this phase. Rendered beside the entered
   * actuals; when they disagree both show and neither wins silently. */
  firstSheetDate: string | null;
  lastSheetDate: string | null;
}

export interface PhaseGanttModel {
  span: CalendarSpan | null;
  rows: PhaseGanttRow[];
}

const num = (v: string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** Date-only string → UTC ms. `new Date('2026-03-06')` parses as UTC but renders local. */
export const dayMs = (s: string): number =>
  Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));

export const dateOnly = (s: string | null | undefined): string | null =>
  s ? s.slice(0, 10) : null;

export const todayUtcMs = (): number => {
  const now = new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
};

/**
 * Planned calendar span — the KPI card's Day X/Y. Dateless phases contribute nothing; FA
 * push is NOT folded in (that would silently change "calendar days of the job" into
 * "calendar days plus projected slip"). The Gantt extends this separately so overlays fit.
 */
export function projectCalendarSpan(
  phases: Pick<ProjectPhase, 'planned_start' | 'planned_end'>[],
  todayMs: number = todayUtcMs(),
): CalendarSpan | null {
  const dated = phases.filter((p) => p.planned_start && p.planned_end);
  if (dated.length === 0) return null;
  const start = Math.min(...dated.map((p) => dayMs(p.planned_start as string)));
  const lastEnd = Math.max(...dated.map((p) => dayMs(p.planned_end as string)));
  const dayY = Math.max(1, Math.round((lastEnd - start) / MS_PER_DAY) + 1);
  const dayX = Math.min(Math.max(Math.floor((todayMs - start) / MS_PER_DAY) + 1, 1), dayY);
  return { dayX, dayY, startMs: start };
}

const dayNumber = (iso: string, startMs: number): number =>
  Math.round((dayMs(iso) - startMs) / MS_PER_DAY) + 1;

const STATUS_LABEL: Record<GanttStatus, string> = {
  green: 'On track',
  yellow: 'At risk',
  red: 'Over budget',
  future: 'Not started',
};

export const LIFECYCLE_LABEL: Record<PhaseLifecycleStatus, string> = {
  future: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

const clampDay = (day: number, lo: number, hi: number): number =>
  Math.min(Math.max(day, lo), hi);

/**
 * The actual bar's geometry: entered start → entered end, or today while the phase is open.
 * Clamped into the span so an early start or a sheet-less future entry cannot paint outside the
 * chart. No entered start = no bar — the bar renders what the PM ENTERED; sheet evidence renders
 * as dates, not geometry.
 */
export function actualBarPct(args: {
  actualStart: string | null;
  actualEnd: string | null;
  span: CalendarSpan | null;
  dayY: number;
  todayMs: number;
}): { leftPct: number; widthPct: number } | null {
  const { actualStart, actualEnd, span, dayY, todayMs } = args;
  if (!actualStart || !span || dayY <= 0) return null;
  const rawStart = dayNumber(actualStart, span.startMs);
  const rawEnd = actualEnd
    ? dayNumber(actualEnd, span.startMs)
    : Math.floor((todayMs - span.startMs) / MS_PER_DAY) + 1;
  // No bar for a day not yet worked, and none for a window wholly outside the span (review F11):
  // the old clamp-up painted a phantom 1-day bar for a future-dated entered start, and a 1-day
  // sliver at the span edge for an out-of-span window.
  if (rawEnd < rawStart || rawEnd < 1 || rawStart > dayY) return null;
  const startDay = clampDay(rawStart, 1, dayY);
  const endDay = clampDay(rawEnd, startDay, dayY);
  return {
    leftPct: ((startDay - 1) / dayY) * 100,
    widthPct: ((endDay - startDay + 1) / dayY) * 100,
  };
}

function matchBva(name: string, rows: BidVsActualRow[] | null): BidVsActualRow | undefined {
  if (!rows) return undefined;
  return rows.find((r) => r.name === name);
}

function phasePercent(phase: ProjectPhase, prod: ProductionRow | undefined): number | null {
  // A missing production row is not 0% — that paints during the fetch and stays if the
  // query errors. Zero metres of a known distance, on a row that DID arrive, is 0%.
  if (!prod) return null;
  const dist = num(phase.distance_m);
  const produced = num(prod.produced_m);
  if (dist === undefined || dist <= 0 || produced === undefined) return null;
  return Math.round(Math.min(100, Math.max(0, (produced / dist) * 100)));
}

function statusOf(args: {
  startMs: number | null;
  todayMs: number;
  projectedVariance: number | undefined;
  slipDays: number;
  pushDays: number;
}): GanttStatus {
  // Compare against the clock, not the clamped Day X — a future window still reports Day 1
  // on the KPI (the conversion-day clamp), but its bars are Not started.
  if (args.startMs === null || args.startMs > args.todayMs) return 'future';
  // Over-budget is a MONEY claim — same sign as the table (projected_variance < 0).
  if (args.projectedVariance !== undefined && args.projectedVariance < 0) return 'red';
  if (args.slipDays > 0 || args.pushDays > 0) return 'yellow';
  return 'green';
}

const displayCostCode = (c: { code: string; external_code?: string | null }): string =>
  (c.external_code && c.external_code.trim()) || c.code;

export function buildPhaseGantt(args: {
  phases: ProjectPhase[];
  production?: ProductionRow[];
  crew?: CrewLoadingRow[];
  cascade?: CascadePhase[];
  /** Permitted BvA rows, or null/omitted for a blind caller — never an empty stand-in. */
  bvaRows?: BidVsActualRow[] | null;
  todayMs?: number;
}): PhaseGanttModel {
  const todayMs = args.todayMs ?? todayUtcMs();
  const { phases, production, crew = [], cascade = [], bvaRows = null } = args;
  const plannedSpan = projectCalendarSpan(phases, todayMs);
  const extra = cascade.reduce((m, c) => Math.max(m, c.days_pushed, c.fa_days), 0);
  const span: CalendarSpan | null = plannedSpan === null ? null : {
    ...plannedSpan,
    // Extra columns so a last-phase slip/push is visible; dayX stays the planned "today".
    dayY: plannedSpan.dayY + extra,
  };
  const prodByName = new Map((production ?? []).map((r) => [r.phase, r]));
  const crewById = new Map(crew.filter((r) => r.phase_id).map((r) => [r.phase_id as string, r]));
  const cascadeById = new Map(cascade.map((c) => [c.phase_id, c]));
  const dayY = span?.dayY ?? 0;

  const rows = [...phases]
    .sort((a, b) => a.sequence - b.sequence)
    .map((p): PhaseGanttRow => {
      const start = dateOnly(p.planned_start);
      const end = dateOnly(p.planned_end);
      const startDay = span && start ? dayNumber(start, span.startMs) : null;
      const endDay = span && end ? dayNumber(end, span.startMs) : null;
      const durationDays = startDay !== null && endDay !== null
        ? endDay - startDay + 1 : null;
      const cas = cascadeById.get(p.id);
      const slipDays = cas?.fa_days ?? 0;
      const pushDays = cas?.days_pushed ?? 0;
      const bva = matchBva(p.name, bvaRows);
      const baselineCodes = (p.cost_codes ?? []).map(displayCostCode);
      const costCodes = baselineCodes.length > 0
        ? baselineCodes
        : (bva?.code ? [bva.code] : []);
      const planned = bva ? num(bva.planned) : undefined;
      const projected = bva ? num(bva.projected) : undefined;
      const projectedVariance = bva ? num(bva.projected_variance) : undefined;
      const status = statusOf({
        startMs: start ? dayMs(start) : null, todayMs, projectedVariance, slipDays, pushDays,
      });
      const crewRow = crewById.get(p.id);
      const actualStart = dateOnly(p.actual_start);
      const actualEnd = dateOnly(p.actual_end);
      const actualBar = actualBarPct({ actualStart, actualEnd, span, dayY, todayMs });
      return {
        id: p.id,
        name: p.name,
        sequence: p.sequence,
        startDay,
        endDay,
        durationDays,
        windowStart: start,
        windowEnd: end,
        leftPct: startDay !== null && dayY > 0 ? ((startDay - 1) / dayY) * 100 : null,
        widthPct: durationDays !== null && dayY > 0 ? (durationDays / dayY) * 100 : null,
        percentComplete: phasePercent(p, prodByName.get(p.name)),
        status,
        statusLabel: STATUS_LABEL[status],
        slipDays,
        pushDays,
        crewPlanned: crewRow ? num(crewRow.planned_headcount) ?? null : null,
        crewActual: crewRow ? num(crewRow.actual_headcount) ?? null : null,
        costCodes,
        costCode: costCodes[0] ?? bva?.code ?? null,
        planned,
        projected,
        projectedVariance,
        actualStart,
        actualEnd,
        actualLeftPct: actualBar?.leftPct ?? null,
        actualWidthPct: actualBar?.widthPct ?? null,
        lifecycle: p.status,
        lifecycleLabel: LIFECYCLE_LABEL[p.status],
        firstSheetDate: dateOnly(p.first_sheet_date),
        lastSheetDate: dateOnly(p.last_sheet_date),
      };
    });

  return { span, rows };
}

/** Overlay geometry: slip starts at planned_end, push starts at planned_end. Clipped to 100%. */
export function overlayPct(
  endDay: number | null,
  days: number,
  dayY: number,
): { leftPct: number; widthPct: number } | null {
  if (endDay === null || days <= 0 || dayY <= 0) return null;
  const leftPct = (endDay / dayY) * 100;
  const widthPct = (days / dayY) * 100;
  if (leftPct >= 100) return null;
  return { leftPct, widthPct: Math.min(widthPct, 100 - leftPct) };
}
